// src/common/ui/heading-groups.js
// Shared logic for rendering footnotes grouped by headings
// Note: We import from the parent ui.js to avoid circular dependency with footnote-cards.js
import { createFootnoteCard } from '../ui.js';
import { renderAuditResults } from './citation-results.js';
import { calculateCitationScore } from './badges-status.js';
import {
  bindStatusFilterChips, countStatusFlags, getStatusFilter,
  notifyStatusResultsChanged, renderStatusFilterChips,
} from './status-filter.js';

/**
 * Calculate verification stats for a heading group based on its footnotes
 * @param {Array} groupItems - Footnotes in the heading group
 * @param {Object} resultsCache - ResultsCache instance
 * @returns {Object} { score: number|null (0-100), scorable: number, verified: number, issues: number, unresolved: number, total: number, footnoteCount: number }
 */
export function calculateHeadingStats(groupItems, resultsCache) {
  const stats = {
    score: null,
    scorable: 0,
    verified: 0,
    issues: 0,
    unresolved: 0,
    total: 0,
    footnoteCount: groupItems?.length || 0
  };

  if (!groupItems || groupItems.length === 0 || !resultsCache) {
    return stats;
  }

  let totalScore = 0;
  let scorableCitations = 0;

  for (const item of groupItems) {
    const footnoteId = String(item.id);
    const cachedResults = resultsCache.getResults?.(footnoteId);

    if (!cachedResults || typeof cachedResults !== 'object') continue;

    for (const [citationId, result] of Object.entries(cachedResults)) {
      if (citationId === '__error__') continue;
      if (!result) continue;

      stats.total++;
      const { score, category } = calculateCitationScore(result);

      if (category === 'verified') {
        stats.verified++;
      } else if (category === 'issues') {
        stats.issues++;
      } else {
        stats.unresolved++;
      }

      // Score inclusion is based on "scorable" outcomes, not category.
      // This lets us include congruence outcomes like `insufficient` while still
      // excluding pure resolution failures like `wrong_document`.
      if (score !== null) {
        totalScore += score;
        scorableCitations++;
      }
    }
  }

  stats.score = scorableCitations > 0 ? Math.round(totalScore / scorableCitations) : null;
  stats.scorable = scorableCitations;
  return stats;
}

/**
 * Get CSS class for score color based on percentage
 * @param {number} score - Score percentage (0-100)
 * @returns {string} CSS class name
 */
function getScoreColorClass(score) {
  if (score >= 80) return 'score-high';
  if (score >= 50) return 'score-medium';
  return 'score-low';
}

/**
 * Create the stats display HTML element
 * @param {Object} stats - Stats object from calculateHeadingStats
 * @returns {HTMLElement} Stats container element
 */
export function createStatsDisplay(stats) {
  const container = document.createElement('div');
  container.className = 'header-stats';

  // If no results yet, show footnote count only
  if (stats.total === 0) {
    const countBadge = document.createElement('span');
    countBadge.className = 'header-count';
    countBadge.textContent = stats.footnoteCount;
    container.appendChild(countBadge);
    return container;
  }

  // Create progress bar
  const barContainer = document.createElement('div');
  barContainer.className = 'stats-bar';

  const barFill = document.createElement('div');
  barFill.className = 'stats-bar-fill';

  if (stats.score !== null) {
    barFill.style.width = `${stats.score}%`;
    barFill.classList.add(getScoreColorClass(stats.score));
  } else {
    // All unresolved - show empty bar
    barFill.style.width = '0%';
  }

  barContainer.appendChild(barFill);
  container.appendChild(barContainer);

  // Create percentage display
  const percentage = document.createElement('span');
  percentage.className = 'stats-percentage';
  percentage.textContent = stats.score !== null ? `${stats.score}%` : '—';
  container.appendChild(percentage);

  // Create mini counts
  const counts = document.createElement('span');
  counts.className = 'stats-counts';

  if (stats.verified > 0) {
    const verifiedCount = document.createElement('span');
    verifiedCount.className = 'stats-count verified';
    verifiedCount.textContent = `✓${stats.verified}`;
    verifiedCount.title = `${stats.verified} verified`;
    counts.appendChild(verifiedCount);
  }

  if (stats.issues > 0) {
    const issuesCount = document.createElement('span');
    issuesCount.className = 'stats-count issues';
    issuesCount.textContent = `✕${stats.issues}`;
    issuesCount.title = `${stats.issues} issues`;
    counts.appendChild(issuesCount);
  }

  if (stats.unresolved > 0) {
    const unresolvedCount = document.createElement('span');
    unresolvedCount.className = 'stats-count unresolved';
    unresolvedCount.textContent = `?${stats.unresolved}`;
    unresolvedCount.title = `${stats.unresolved} unresolved`;
    counts.appendChild(unresolvedCount);
  }

  container.appendChild(counts);
  return container;
}

/**
 * Update an existing header's stats display
 * @param {HTMLElement} headerEl - The header element to update
 * @param {Object} stats - Stats object from calculateHeadingStats
 */
export function updateHeadingStats(headerEl, stats) {
  if (!headerEl) return;

  // Find or create the stats container
  const headerContent = headerEl.querySelector('.header-content');
  if (!headerContent) return;

  // Remove existing stats container if present
  const existingStats = headerContent.querySelector('.header-stats');
  if (existingStats) {
    existingStats.remove();
  }

  // Also remove old header-count if present (we're replacing it with stats)
  const existingCount = headerContent.querySelector('.header-count');
  if (existingCount) {
    existingCount.remove();
  }

  // Create and add new stats display
  const newStats = createStatsDisplay(stats);
  headerContent.appendChild(newStats);

  // Store stats in data attribute for potential recursive rollup
  headerEl.dataset.stats = JSON.stringify(stats);
}

/**
 * Create a hierarchical heading header element
 * @param {Object} heading - Heading object with id, text, level
 * @param {Array} groupItems - Footnotes in this group
 * @param {number} level - Heading level (1-6, 0 for no-heading)
 * @param {HTMLElement} parentContent - Optional parent content container for hierarchical folding
 * @param {Object} options - Options object
 * @param {Object} options.mgr - Optional FootnotesManager instance
 * @param {HTMLElement} options.footnoteListEl - Optional footnote list container
 * @param {Object} options.handlers - Optional event handlers { onNavigate, onVerify }
 * @returns {Object} { headerEl, contentEl }
 */
export function createHierarchicalHeader(heading, groupItems, level, parentContent = null, options = {}) {
  const { mgr, footnoteListEl, handlers: providedHandlers } = options;
  
  // Create collapsible header
  const headerEl = document.createElement("div");
  headerEl.className = "footnote-group-header";
  headerEl.setAttribute("aria-expanded", "true");
  
  // Add heading level and group attributes
  if (level === 0) {
    headerEl.setAttribute("data-group", "no-heading");
    headerEl.setAttribute("data-level", "0");
  } else {
    headerEl.setAttribute("data-level", String(level));
  }
  
  const headerContent = document.createElement("div");
  headerContent.className = "header-content";
  
  const chev = document.createElement("span");
  chev.className = "chev";
  chev.textContent = "›";
  
  const headerText = document.createElement("div");
  headerText.className = "header-text";
  
  const headerTitle = document.createElement("h4");
  headerTitle.className = "header-title";
  headerTitle.textContent = heading ? heading.text : "Citations";
  
  const headerMetaRow = document.createElement("div");
  headerMetaRow.className = "header-meta-row";

  const headerMeta = document.createElement("span");
  headerMeta.className = "header-meta";
  // Only show page meta if heading has page info (Word docs don't have pages)
  if (heading?.page !== undefined) {
    headerMeta.textContent = `Page ${heading.page}`;
  } else {
    headerMeta.classList.add('is-empty');
    headerMeta.setAttribute('aria-hidden', 'true');
  }

  const verifyGroupBtn = document.createElement('button');
  verifyGroupBtn.className = 'header-verify-btn';
  verifyGroupBtn.type = 'button';
  verifyGroupBtn.textContent = 'Verify group';
  verifyGroupBtn.title = 'Verify only citations in this group';
  verifyGroupBtn.style.display = 'none';

  headerMetaRow.append(headerMeta, verifyGroupBtn);
  headerText.append(headerTitle, headerMetaRow);
  headerContent.append(chev, headerText);

  // Calculate and add stats display (will be updated later if mgr has results)
  const effectiveMgrForStats = mgr || window.footcheckFootnotesManager;
  const stats = calculateHeadingStats(groupItems, effectiveMgrForStats?.resultsCache);
  const statsDisplay = createStatsDisplay(stats);
  headerContent.appendChild(statsDisplay);

  // Store heading ID and group items for later stats updates
  headerEl.dataset.headingId = heading?.id || 'no-heading';
  headerEl.dataset.footnoteIds = JSON.stringify((groupItems || []).map(item => item.id));
  headerEl.dataset.stats = JSON.stringify(stats);

  headerEl.appendChild(headerContent);
  
  // Create content container for footnotes
  const contentEl = document.createElement("div");
  contentEl.className = "footnote-group-content show";

  // Enable the group verify button only when there are citations under this heading
  setTimeout(() => {
    if (!verifyGroupBtn.isConnected) return;
    const hasCards = contentEl.querySelector('.card[data-id]');
    const mgrForVerify = mgr || window.footcheckFootnotesManager;
    const canVerify = !!mgrForVerify?.verifyAllSmart;
    verifyGroupBtn.style.display = hasCards && canVerify ? '' : 'none';
  }, 0);
  
  // Add click handler for header with hierarchical folding
  headerEl.addEventListener("click", () => {
    const isExpanded = headerEl.getAttribute("aria-expanded") === "true";
    headerEl.setAttribute("aria-expanded", String(!isExpanded));
    contentEl.classList.toggle("show", !isExpanded);
    
    // If this is a parent header, also control child headers
    if (parentContent && level === 1) {
      const childHeaders = contentEl.querySelectorAll('.footnote-group-header');
      childHeaders.forEach(childHeader => {
        childHeader.setAttribute("aria-expanded", String(!isExpanded));
        const childContent = childHeader.nextElementSibling;
        if (childContent && childContent.classList.contains('footnote-group-content')) {
          childContent.classList.toggle("show", !isExpanded);
        }
      });
    }
  });
  
  // Add keyboard accessibility
  headerEl.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      headerEl.click();
    }
  });
  
  // Make header focusable
  headerEl.setAttribute("tabindex", "0");
  headerEl.setAttribute("role", "button");
  headerEl.setAttribute("aria-label", `Toggle ${heading ? heading.text : 'Citations'} group`);

  // Verify only the citations inside this heading group
  verifyGroupBtn.addEventListener('click', async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const mgrForVerify = mgr || window.footcheckFootnotesManager;
    if (!mgrForVerify?.verifyAllSmart) return;
    const cards = Array.from(contentEl.querySelectorAll('.card[data-id]'));
    const footnoteIds = cards.map(card => card.dataset.id).filter(Boolean);
    if (footnoteIds.length === 0) return;
    verifyGroupBtn.disabled = true;
    verifyGroupBtn.classList.add('is-busy');
    try {
      // First fill any unchecked citations in this group. Once the group is
      // complete, a subsequent click rechecks it while preserving successful
      // source resolutions (failed resolutions are retried by the verifier).
      // Citation-type filters still define the group's active scope.
      await mgrForVerify.verifyAllSmart({ footnoteIds, recheckIfComplete: true });
    } finally {
      verifyGroupBtn.disabled = false;
      verifyGroupBtn.classList.remove('is-busy');
    }
  });
  
  // Get or create handlers
  let handlers = providedHandlers;
  if (!handlers && mgr) {
    // Create handlers from mgr (desktop app style)
    handlers = {
      onNavigate: (footnoteId) => {
        const item = mgr.items?.find(it => it.id === footnoteId);
        if (item && mgr.focusRight) {
          mgr.focusRight(item);
        }
      },
      onVerify: (footnoteId, card) => {
        if (mgr.verifyFootnote) {
          mgr.verifyFootnote(footnoteId, card, footnoteListEl);
        }
      },
    };
  } else if (!handlers) {
    // Fallback: try to get from window (Word add-in style)
    const windowMgr = window.footcheckFootnotesManager;
    if (windowMgr) {
      handlers = {
        onNavigate: (footnoteId) => {
          const item = windowMgr.items?.find(it => it.id === footnoteId);
          if (item && windowMgr.focusRight) {
            windowMgr.focusRight(item);
          }
        },
        onVerify: (footnoteId, card) => {
          if (windowMgr.verifyFootnote) {
            windowMgr.verifyFootnote(footnoteId, card, footnoteListEl);
          }
        },
      };
    } else {
      // No handlers available
      handlers = {};
    }
  }
  
  // Get mgr for cached results (use provided mgr or window fallback)
  const effectiveMgr = mgr || window.footcheckFootnotesManager;
  
  // Create footnote cards
  for (const item of (groupItems || [])) {
    const cardEl = createFootnoteCard(item, handlers);
    contentEl.appendChild(cardEl);
    
    // Auto-render cached results if available
    if (effectiveMgr) {
      const footnoteId = String(item.id);
      const cachedResults = effectiveMgr.resultsCache?.getResults?.(footnoteId);
      // Results may be split between the parsed item and the persisted cache
      // when only some citations have been resolved. Keep both sets, with the
      // cache winning for the same citation.
      const itemResults = item.cached_results && typeof item.cached_results === 'object'
        ? item.cached_results
        : {};
      const resultsToRender = (cachedResults || itemResults) && {
        ...itemResults,
        ...(cachedResults && typeof cachedResults === 'object' ? cachedResults : {}),
      };
      
      // If we have cached_results on the item but not in resultsCache, store them now
      let finalResultsToRender = resultsToRender;
      if (!cachedResults && item.cached_results && Object.keys(item.cached_results).length > 0) {
        console.log(`[AUTO-RENDER] Storing cached_results for footnote ${footnoteId} from item.cached_results`);
        effectiveMgr.resultsCache?.storeResults?.(footnoteId, item.cached_results);
        // Update resultsToRender to use the newly stored cached results
        const updatedResults = effectiveMgr.resultsCache?.getResults?.(footnoteId);
        if (updatedResults) {
          finalResultsToRender = updatedResults;
        }
      }
      
      if (finalResultsToRender && Object.keys(finalResultsToRender).length > 0) {
        console.log(`[AUTO-RENDER] Rendering cached results for footnote ${footnoteId}:`, Object.keys(finalResultsToRender).length, 'entries');
        const resultsContainer = cardEl.querySelector('.results');
        console.log(`[AUTO-RENDER] Results container found:`, !!resultsContainer, 'for card:', cardEl);
        if (resultsContainer) {
          // Ensure file catalog is loaded
          if (effectiveMgr.fileCatalog && effectiveMgr.fileCatalog.size === 0) {
            effectiveMgr.ensureFileCatalog?.().catch(err => {
              console.warn('[AUTO-RENDER] Failed to load file catalog:', err);
            });
          }
          
          const resolveFileDisplay = (data, cid) => {
            if (effectiveMgr.getFileDisplay) {
              const result = effectiveMgr.getFileDisplay(data);
              if (result?.label) {
                const filename = result.label.split(/[\\/]/).pop();
                return { ...result, label: filename || result.label };
              }
              return result;
            }
            const fileId = data?.file_id || data?.file;
            const filePath = data?.file || data?.file_path || data?.resolve_result?.file_path;
            let label = '—';
            if (filePath) {
              const filename = filePath.split(/[\\/]/).pop();
              label = filename || filePath;
            } else if (fileId && effectiveMgr.fileCatalog && (data?.source_db || data?.resolve_result?.source_db || 'doc') === 'doc') {
              const fileInfo = effectiveMgr.fileCatalog.get(String(fileId));
              if (fileInfo?.path) {
                const filename = fileInfo.path.split(/[\\/]/).pop();
                label = filename || fileInfo.path;
              } else if (fileInfo?.title) {
                label = fileInfo.title;
              } else {
                label = `File ${fileId}`;
              }
            } else if (fileId) {
              label = `File ${fileId}`;
            }
            return { fileId: fileId ? String(fileId) : null, label };
          };
          
          const getFileSourceUrl = (fileId, sourceDb) => {
            if (effectiveMgr.getFileSourceUrl) {
              return effectiveMgr.getFileSourceUrl(fileId, sourceDb);
            }
            return null;
          };
          
          // Pass footnoteListEl (the root container) so renderAuditResults can find the card
          // The card is nested inside heading groups, but renderAuditResults will search for it
          const rootContainer = footnoteListEl || cardEl.closest('#footnote-list') || document.body;
          console.log(`[AUTO-RENDER] Rendering for footnote ${footnoteId}, using container:`, rootContainer, 'cardEl:', cardEl);
          console.log(`[AUTO-RENDER] Card data-id:`, cardEl.dataset.id, 'item.id:', item.id);
          
          // Use setTimeout to ensure DOM is fully updated
          setTimeout(() => {
            renderAuditResults(rootContainer, item.id, finalResultsToRender, {
              resolveFileDisplay,
              getFileSourceUrl,
              onChangeFile: (citeId) => {
                if (effectiveMgr.reassignCitationFile) {
                  effectiveMgr.reassignCitationFile(item.id, citeId);
                }
              },
              onResetCitation: (citeId, interaction) => {
                if (effectiveMgr.resetCitationResult) {
                  effectiveMgr.resetCitationResult(item.id, citeId, interaction);
                }
              },
            });
          }, 0);
          
          const verifyBtn = cardEl.querySelector('.card-body .footnote-actions button');
          if (verifyBtn) {
            verifyBtn.textContent = 'Check Again';
          }
        } else {
          console.warn(`[AUTO-RENDER] No results container found for footnote ${footnoteId}`);
        }
      } else {
        console.log(`[AUTO-RENDER] No cached results to render for footnote ${footnoteId}, cachedResults:`, !!cachedResults, 'item.cached_results:', !!item.cached_results);
      }
    }
  }
  
  return { headerEl, contentEl };
}

/**
 * Recursively calculate stats for a heading by aggregating:
 * 1. Stats from its direct footnotes
 * 2. Stats from all child headings (recursively)
 * @param {HTMLElement} headerEl - The heading header element
 * @param {Object} resultsCache - ResultsCache instance
 * @param {Map} itemMap - Map of footnote items by ID
 * @returns {Object} Aggregated stats
 */
function calculateRecursiveHeadingStats(headerEl, resultsCache, itemMap) {
  const stats = {
    score: null,
    scorable: 0,
    verified: 0,
    issues: 0,
    unresolved: 0,
    total: 0,
    footnoteCount: 0
  };

  // Get the content element for this header (immediately following sibling)
  const contentEl = headerEl.nextElementSibling;
  if (!contentEl || !contentEl.classList.contains('footnote-group-content')) {
    // No content, just use direct footnote stats
    const footnoteIdsJson = headerEl.dataset.footnoteIds;
    if (footnoteIdsJson) {
      try {
        const footnoteIds = JSON.parse(footnoteIdsJson);
        const groupItems = footnoteIds.map(id => itemMap.get(String(id))).filter(Boolean);
        return calculateHeadingStats(groupItems, resultsCache);
      } catch (e) {
        return stats;
      }
    }
    return stats;
  }

  // First, get stats from direct footnotes of this heading
  const footnoteIdsJson = headerEl.dataset.footnoteIds;
  let totalScore = 0;
  let scorableCitations = 0;

  if (footnoteIdsJson) {
    try {
      const footnoteIds = JSON.parse(footnoteIdsJson);
      const groupItems = footnoteIds.map(id => itemMap.get(String(id))).filter(Boolean);
      const directStats = calculateHeadingStats(groupItems, resultsCache);

      stats.verified += directStats.verified;
      stats.issues += directStats.issues;
      stats.unresolved += directStats.unresolved;
      stats.total += directStats.total;
      stats.footnoteCount += directStats.footnoteCount;
      stats.scorable += directStats.scorable || 0;

      // Accumulate for score calculation
      if (directStats.score !== null && (directStats.scorable || 0) > 0) {
        totalScore += directStats.score * directStats.scorable;
        scorableCitations += directStats.scorable;
      }
    } catch (e) {
      console.warn('[HEADING-GROUPS] Failed to parse footnote IDs:', e);
    }
  }

  // Then, recursively get stats from child headings
  // Use Array.from + filter to get direct children (more reliable than :scope)
  const childHeaders = Array.from(contentEl.children).filter(
    el => el.classList.contains('footnote-group-header')
  );
  for (const childHeader of childHeaders) {
    const childStats = calculateRecursiveHeadingStats(childHeader, resultsCache, itemMap);

    stats.verified += childStats.verified;
    stats.issues += childStats.issues;
    stats.unresolved += childStats.unresolved;
    stats.total += childStats.total;
    stats.footnoteCount += childStats.footnoteCount;
    stats.scorable += childStats.scorable || 0;

    // Accumulate for score calculation
    if (childStats.score !== null && (childStats.scorable || 0) > 0) {
      totalScore += childStats.score * childStats.scorable;
      scorableCitations += childStats.scorable;
    }
  }

  // Calculate final score
  stats.score = scorableCitations > 0 ? Math.round(totalScore / scorableCitations) : null;

  return stats;
}

/**
 * Recursively update a header and all its descendant headers (bottom-up)
 * This ensures child stats are calculated before parent stats
 * @param {HTMLElement} headerEl - The heading header element
 * @param {Object} resultsCache - ResultsCache instance
 * @param {Map} itemMap - Map of footnote items by ID
 */
function updateHeaderAndDescendants(headerEl, resultsCache, itemMap) {
  // Get the content element for this header
  const contentEl = headerEl.nextElementSibling;

  if (contentEl && contentEl.classList.contains('footnote-group-content')) {
    // First, recursively update all child headers (bottom-up approach)
    const childHeaders = Array.from(contentEl.children).filter(
      el => el.classList.contains('footnote-group-header')
    );

    for (const childHeader of childHeaders) {
      updateHeaderAndDescendants(childHeader, resultsCache, itemMap);
    }
  }

  // Now update this header with aggregated stats from children
  const stats = calculateRecursiveHeadingStats(headerEl, resultsCache, itemMap);
  updateHeadingStats(headerEl, stats);
}

/**
 * Refresh stats for all heading headers in a container (recursive)
 * @param {HTMLElement} container - Container element with heading headers
 * @param {Object} resultsCache - ResultsCache instance
 * @param {Array} items - Array of all footnote items (to look up by ID)
 */
export function refreshAllHeadingStats(container, resultsCache, items = []) {
  if (!container || !resultsCache) return;

  // Create a map of footnote items by ID for quick lookup
  const itemMap = new Map(items.map(item => [String(item.id), item]));

  // Find only top-level heading headers (direct children of container)
  // Each header will recursively calculate stats for itself and all children
  const topLevelHeaders = Array.from(container.children).filter(
    el => el.classList.contains('footnote-group-header')
  );

  // For each top-level header, update it recursively
  // The recursive function will update all descendant headers too
  for (const headerEl of topLevelHeaders) {
    try {
      updateHeaderAndDescendants(headerEl, resultsCache, itemMap);
    } catch (err) {
      console.warn('[HEADING-GROUPS] Failed to refresh stats for header:', err);
    }
  }

  // Update document-level stats
  updateDocumentStats(container, resultsCache, items);
}

/**
 * Refresh stats for a single footnote's parent heading and all ancestors
 * Called after a footnote's results are updated
 * @param {HTMLElement} container - Container element
 * @param {string|number} footnoteId - The footnote ID that was updated
 * @param {Object} resultsCache - ResultsCache instance
 * @param {Array} items - Array of all footnote items
 */
export function refreshHeadingStatsForFootnote(container, footnoteId, resultsCache, items = []) {
  if (!container || !resultsCache) return;

  const targetId = String(footnoteId);
  const itemMap = new Map(items.map(item => [String(item.id), item]));

  // Find the card for this footnote
  const escapedId = CSS?.escape ? CSS.escape(targetId) : targetId;
  const card = container.querySelector(`.card[data-id="${escapedId}"]`);

  if (card) {
    // Walk up the DOM to find all ancestor heading headers
    let currentEl = card.parentElement;
    const ancestorHeaders = [];

    while (currentEl && currentEl !== container) {
      // Check if we're in a footnote-group-content
      if (currentEl.classList.contains('footnote-group-content')) {
        // The header is the previous sibling
        const headerEl = currentEl.previousElementSibling;
        if (headerEl && headerEl.classList.contains('footnote-group-header')) {
          ancestorHeaders.push(headerEl);
        }
      }
      currentEl = currentEl.parentElement;
    }

    // Update all ancestor headers (from innermost to outermost)
    for (const headerEl of ancestorHeaders) {
      try {
        const stats = calculateRecursiveHeadingStats(headerEl, resultsCache, itemMap);
        updateHeadingStats(headerEl, stats);
      } catch (err) {
        console.warn('[HEADING-GROUPS] Failed to refresh stats for ancestor header:', err);
      }
    }
  } else {
    // Card not found - this can happen during initial render
    // Fall back to refreshing all headers
    const allHeaders = Array.from(container.querySelectorAll('.footnote-group-header'));
    for (const headerEl of allHeaders) {
      try {
        const stats = calculateRecursiveHeadingStats(headerEl, resultsCache, itemMap);
        updateHeadingStats(headerEl, stats);
      } catch (err) {
        console.warn('[HEADING-GROUPS] Failed to refresh stats for header:', err);
      }
    }
  }

  // Always update document-level stats
  updateDocumentStats(container, resultsCache, items);
}

/**
 * Create or update the document-level stats summary
 * @param {HTMLElement} container - Container element
 * @param {Object} resultsCache - ResultsCache instance
 * @param {Array} items - Array of all footnote items
 */
export function updateDocumentStats(container, resultsCache, items = []) {
  if (!container || !resultsCache) return;

  // Calculate document-wide stats
  const docStats = resultsCache.calculateDetailedSummary();
  docStats.footnoteCount = items.length;

  // Find or create the document stats element
  let docStatsEl = container.querySelector('.document-stats');
  if (!docStatsEl) {
    docStatsEl = document.createElement('div');
    docStatsEl.className = 'document-stats';
    // Insert at the top of the container (after any filter panel)
    const filterPanel = container.querySelector('.citation-type-filter-panel');
    if (filterPanel) {
      filterPanel.after(docStatsEl);
    } else {
      container.prepend(docStatsEl);
    }
  }

  // Only show if there are any results
  if (docStats.total === 0) {
    docStatsEl.style.display = 'none';
    return;
  }

  docStatsEl.style.display = '';

  // Create the stats display
  const scoreClass = docStats.score !== null
    ? (docStats.score >= 80 ? 'score-high' : docStats.score >= 50 ? 'score-medium' : 'score-low')
    : '';

  const scoreText = docStats.score !== null ? `${docStats.score}%` : '—';

  // Counts double as status filters: clicking one shows only those citations.
  const flags = countStatusFlags(resultsCache);
  docStatsEl._statusCounts = {
    verified: docStats.verified,
    issues: docStats.issues,
    unresolved: docStats.unresolved,
    hallucination: flags.hallucination,
    quote: flags.quote,
  };
  docStatsEl.classList.toggle('has-status-filter', !!getStatusFilter());
  bindStatusFilterChips(docStatsEl);

  docStatsEl.innerHTML = `
    <div class="document-stats-content">
      <span class="document-stats-label">Document Verification</span>
      <div class="document-stats-bar">
        <div class="stats-bar">
          <div class="stats-bar-fill ${scoreClass}" style="width: ${docStats.score || 0}%"></div>
        </div>
        <span class="stats-percentage">${scoreText}</span>
      </div>
      <span class="stats-counts">${renderStatusFilterChips(docStatsEl._statusCounts)}</span>
      <span class="document-stats-total">${docStats.total} citations</span>
    </div>
  `;

  notifyStatusResultsChanged();
}
